import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, TrendingUp, Sparkles, Globe, Wallet, Shield, Newspaper, ExternalLink, TrendingDown, Minus } from "lucide-react";
import client, { fmtUsd, fmtPct } from "../lib/api";
import Sparkline from "../components/Sparkline";

const SENT_STYLES = {
  very_bearish: { bg: "rgba(239,68,68,0.18)", border: "rgba(239,68,68,0.40)", color: "#fecaca", Icon: TrendingDown },
  bearish:      { bg: "rgba(239,68,68,0.10)", border: "rgba(239,68,68,0.25)", color: "#fca5a5", Icon: TrendingDown },
  neutral:      { bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.10)", color: "#a3a3a3", Icon: Minus },
  bullish:      { bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.25)", color: "#86efac", Icon: TrendingUp },
  very_bullish: { bg: "rgba(16,185,129,0.18)", border: "rgba(16,185,129,0.40)", color: "#bbf7d0", Icon: TrendingUp },
};

const Landing = () => {
  const [coins, setCoins] = useState([]);
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);

  useEffect(() => {
    client.get("/crypto/markets?per_page=6").then(({ data }) => setCoins(data.results || [])).catch(() => {});
    client.get("/news?with_sentiment=true&category=business").then(({ data }) => {
      setNews((data.articles || []).slice(0, 6));
      setNewsLoading(false);
    }).catch(() => setNewsLoading(false));
  }, []);

  return (
    <div className="space-y-24">
      {/* Hero */}
      <section className="pt-12 pb-8 fade-in-up">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-8">
            <span className="chip" data-testid="hero-chip">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live · Stocks · Crypto · NFTs
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Your <span className="italic font-light">liquid</span> view <br />
              of the <span className="text-neutral-400">world's</span> markets.
            </h1>
            <p className="text-neutral-400 text-lg max-w-xl leading-relaxed">
              Track equities, crypto and NFTs in one elegant glass interface. Real-time prices, contextual news, and Claude-powered insights to make sharper decisions.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/dashboard" data-testid="hero-cta-dashboard" className="btn-primary inline-flex items-center gap-2">
                Enter Dashboard <ArrowUpRight size={16} />
              </Link>
              <Link to="/nfts" data-testid="hero-cta-nfts" className="btn-glass inline-flex items-center gap-2">
                Explore NFTs
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="glass p-6 relative overflow-hidden float-slow">
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-blue-500/30 blur-3xl" />
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500">Portfolio Snapshot</div>
                  <div className="font-display text-3xl mt-1 font-mono-num">$184,257.42</div>
                </div>
                <div className="text-emerald-400 text-sm font-mono-num">+2.41%</div>
              </div>
              <div className="space-y-3">
                {coins.slice(0, 4).map((c) => (
                  <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                    <img src={c.image} alt={c.name} className="w-7 h-7 rounded-full" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{c.name}</div>
                      <div className="text-xs text-neutral-500 uppercase">{c.symbol}</div>
                    </div>
                    {c.sparkline_in_7d?.price && (
                      <Sparkline data={c.sparkline_in_7d.price.slice(-30)} width={80} height={24} />
                    )}
                    <div className="text-right">
                      <div className="font-mono-num text-sm">{fmtUsd(c.current_price)}</div>
                      <div className={`text-xs font-mono-num ${c.price_change_percentage_24h >= 0 ? "up" : "down"}`}>
                        {fmtPct(c.price_change_percentage_24h)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="space-y-8">
        <div className="flex items-end justify-between">
          <div>
            <span className="chip">Why Wealth Builders</span>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-4">A single source of truth for global wealth.</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Globe, title: "Unified Markets", desc: "World stocks via Alpha Vantage and 10k+ crypto via CoinGecko, side by side." },
            { icon: Sparkles, title: "Claude AI Insights", desc: "Asset-aware buy / hold / sell suggestions grounded in live news context." },
            { icon: Wallet, title: "Web3 Native", desc: "Connect MetaMask, view your NFTs across OpenSea, and track wallet holdings." },
            { icon: TrendingUp, title: "Live News Stream", desc: "Asset-tagged news from trusted sources, refreshed every minute." },
            { icon: Shield, title: "Glass UI", desc: "Apple-grade liquid-glass design — calm, precise, futuristic." },
            { icon: ArrowUpRight, title: "Watchlist", desc: "Sign in with Google to save and sync your favourite assets." },
          ].map((f) => (
            <div key={f.title} className="glass glass-hover p-6">
              <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                <f.icon size={18} />
              </div>
              <div className="font-display text-xl">{f.title}</div>
              <p className="text-neutral-400 text-sm mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* News Feed */}
      <section className="space-y-8" data-testid="landing-news">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="chip"><Newspaper size={11} /> Live News</span>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-4">What the market is saying right now.</h2>
            <p className="text-neutral-400 text-sm mt-2 max-w-xl">Top business and crypto headlines, scored for sentiment by Claude.</p>
          </div>
          <Link to="/news" data-testid="landing-news-all" className="btn-glass inline-flex items-center gap-2 text-sm">
            All news <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {newsLoading && Array.from({ length: 6 }).map((_, i) => (
            <div key={`nsk-${i}`} className="glass p-6 h-48 animate-pulse" />
          ))}
          {!newsLoading && news.map((a) => {
            const sty = a.sentiment ? (SENT_STYLES[a.sentiment.label] || SENT_STYLES.neutral) : null;
            const Icon = sty?.Icon;
            return (
              <a
                key={a.url}
                href={a.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`landing-news-${a.url}`}
                className="glass glass-hover overflow-hidden block transition-all"
                style={sty ? { borderColor: sty.border } : {}}
              >
                {a.image && (
                  <img src={a.image} alt="" className="w-full h-36 object-cover" onError={(e) => (e.target.style.display = "none")} />
                )}
                <div className="p-5">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 truncate">{a.source}</div>
                    {sty && (
                      <div
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-mono-num"
                        style={{ background: sty.bg, border: `1px solid ${sty.border}`, color: sty.color }}
                      >
                        <Icon size={9} />
                        {a.sentiment.label.replace("_", " ")}
                      </div>
                    )}
                  </div>
                  <div className="font-display text-base leading-snug line-clamp-3 group-hover:text-blue-400">{a.title}</div>
                  <div className="flex items-center justify-between mt-3 text-[10px] text-neutral-500">
                    <span>{a.published_at ? new Date(a.published_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                    <ExternalLink size={10} />
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="glass-strong p-10 lg:p-16 relative overflow-hidden">
          <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(59,130,246,0.45), transparent 50%)" }} />
          <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-3xl sm:text-4xl tracking-tight">Built for the next generation of investors.</h3>
              <p className="text-neutral-400 mt-3 max-w-xl">Crypto-native. Stock-aware. NFT-curious. Start tracking everything in one place.</p>
            </div>
            <Link to="/dashboard" data-testid="cta-bottom-dashboard" className="btn-primary inline-flex items-center gap-2 whitespace-nowrap">
              Open Wealth Builders Dashboard <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
